import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Globe, ArrowRight, Check, ArrowLeft, Loader } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sendLeadNotification } from '../utils/emailService';

const ScheduleModal = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        country: '',
        contactNumber: '',
        email: '',
        company: '',
        message: '',
        date: '',
        time: '',
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSubmitted(false);
            setFormData(prev => ({
                ...prev,
                userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }));
        }
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleNext = (e) => {
        e.preventDefault();
        if (step === 1) {
            // Basic validation
            if (!formData.name || !formData.country || !formData.contactNumber || !formData.email || !formData.message) {
                alert("Please fill in all required fields.");
                return;
            }
            setStep(2);
        }
    };

    const handleBack = () => {
        setStep(prev => prev - 1);
    };

    const calculatePST = (dateString, timezone) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            // Verify valid date
            if (isNaN(date.getTime())) return null;

            return date.toLocaleString('en-US', {
                timeZone: 'Asia/Manila',
                dateStyle: 'full',
                timeStyle: 'short'
            });
        } catch (e) {
            console.error("Date conversion error", e);
            return "Error calculating time";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Save to Firestore
            const selectedDate = `${formData.date}T${formData.time}`;
            await addDoc(collection(db, 'appointments'), {
                ...formData,
                selectedDate,
                status: 'pending',
                createdAt: serverTimestamp(),
                pstTime: calculatePST(selectedDate, formData.userTimezone)
            });

            // Send Notification Email
            // Note: We format the message to indicate this is a scheduled call
            const emailData = {
                ...formData,
                message: `New Scheduled Call Request:\n\nDate: ${formData.date}\nTime: ${formData.time}\nTimezone: ${formData.userTimezone}\n\nUser Message: ${formData.message}`,
                source: 'Schedule Call Modal'
            };
            await sendLeadNotification(emailData);

            setSubmitted(true);
            setTimeout(() => {
                onClose();
            }, 3000);
        } catch (error) {
            console.error("Error booking appointment:", error);
            alert("Failed to book appointment. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-lg p-0 overflow-hidden shadow-2xl scale-100 animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="bg-[#20222D] p-6 flex justify-between items-center">
                    <div>
                        <h3 className="text-xl font-bold text-white">Schedule a Call</h3>
                        <p className="text-gray-400 text-sm">Step {step} of 2</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-8">
                    {submitted ? (
                        <div className="text-center py-10">
                            <div className="w-16 h-16 bg-[#A2CB52]/20 text-[#A2CB52] rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#20222D] mb-2">Booking Confirmed!</h3>
                            <p className="text-gray-500">We've received your request. Our team will contact you shortly to confirm the details.</p>
                        </div>
                    ) : (
                        <form onSubmit={step === 2 ? handleSubmit : handleNext}>
                            {step === 1 && (
                                <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1">Country *</label>
                                            <input
                                                type="text"
                                                name="country"
                                                required
                                                value={formData.country}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all"
                                                placeholder="USA"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
                                            <input
                                                type="tel"
                                                name="contactNumber"
                                                required
                                                value={formData.contactNumber}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all"
                                                placeholder="+1 (555) 000-0000"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address *</label>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all"
                                            placeholder="john@company.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Company Name</label>
                                        <input
                                            type="text"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all"
                                            placeholder="Acme Corp"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">How can we help? *</label>
                                        <textarea
                                            name="message"
                                            required
                                            rows="3"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all resize-none"
                                            placeholder="Briefly describe your needs..."
                                        ></textarea>
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                                    <div className="bg-[#ECF5DC] p-4 rounded-xl border border-[#A2CB52]/20">
                                        <div className="flex items-center gap-2 text-[#20222D] font-bold mb-2">
                                            <Globe size={18} />
                                            <span>Your Timezone Detected</span>
                                        </div>
                                        <p className="text-sm text-gray-600">
                                            {formData.userTimezone}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Date</label>
                                            <div className="relative">
                                                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                                <input
                                                    type="date"
                                                    name="date"
                                                    required
                                                    min={new Date().toISOString().split('T')[0]}
                                                    value={formData.date || ''}
                                                    onChange={handleChange}
                                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Time</label>
                                            <div className="relative">
                                                <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                                <select
                                                    name="time"
                                                    required
                                                    value={formData.time || ''}
                                                    onChange={handleChange}
                                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all appearance-none"
                                                >
                                                    <option value="">Select time...</option>
                                                    {Array.from({ length: 96 }).map((_, i) => {
                                                        const h = Math.floor(i / 4);
                                                        const m = (i % 4) * 15;
                                                        const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                                                        return (
                                                            <option key={timeStr} value={timeStr}>
                                                                {new Date(`2000-01-01T${timeStr}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {formData.date && formData.time && (
                                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Philippine Standard Time (Manila)</p>
                                            <div className="flex items-center gap-2 text-[#20222D] font-medium">
                                                <Clock size={16} className="text-[#A2CB52]" />
                                                {calculatePST(`${formData.date}T${formData.time}`, formData.userTimezone)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mt-8 flex gap-3">
                                {step === 2 && (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="px-6 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-colors flex items-center"
                                    >
                                        <ArrowLeft size={18} className="mr-2" /> Back
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={loading || (step === 2 && (!formData.date || !formData.time))}
                                    className="flex-1 bg-[#20222D] text-white font-bold py-3 rounded-xl hover:bg-[#2a2d3d] transition-all shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <Loader size={18} className="animate-spin mr-2" />
                                            Processing...
                                        </>
                                    ) : step === 1 ? (
                                        <>
                                            Next Step <ArrowRight size={18} className="ml-2" />
                                        </>
                                    ) : (
                                        'Confirm Booking'
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScheduleModal;
